import React, { useState, useMemo } from 'react';
import { Bar } from 'react-chartjs-2';
import { FiUserPlus } from 'react-icons/fi';

const DailySignupChart = ({ signupGraphData, isLoading }) => {
  const [tooltip, setTooltip] = useState({
    visible: false,
    x: 0,
    y: 0,
    title: '',
    value: '',
    details: null,
    xAlign: 'center',
  });

  const getOptions = () => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: { intersect: false, mode: 'index' },
    plugins: {
      legend: {
        display: true,
        position: 'top',
        align: 'end',
        labels: { boxWidth: 10, usePointStyle: true, font: { size: 11 } }
      },
      tooltip: {
        enabled: false, // Custom HTML Tooltip
        external: (context) => {
          const tooltipModel = context.tooltip;
          if (tooltipModel.opacity === 0) {
            setTooltip((prev) => (prev.visible ? { ...prev, visible: false } : prev));
            return;
          }

          const item = tooltipModel.dataPoints?.[0];
          if (!item) return;

          const details = item.dataset.dailyDetails?.[item.dataIndex];
          setTooltip({
            visible: true,
            x: tooltipModel.caretX,
            y: tooltipModel.caretY - 12,
            title: tooltipModel.title?.[0] || '',
            value: item.formattedValue,
            details,
            xAlign: tooltipModel.xAlign || 'center',
          });
        }
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        stacked: true,
        grid: { color: '#F3F4F6', drawBorder: false },
        ticks: { color: '#9CA3AF', font: { size: 11 }, maxTicksLimit: 6, precision: 0 },
        border: { display: false },
      },
      x: {
        stacked: true,
        grid: { display: false },
        ticks: { color: '#9CA3AF', font: { size: 11 }, maxTicksLimit: 8 },
        border: { display: false },
      },
    },
  });

  const options = useMemo(() => getOptions(), []);

  const transformStyle = useMemo(() => {
    if (tooltip.xAlign === 'left') return 'translate(-15%, -100%)';
    if (tooltip.xAlign === 'right') return 'translate(-85%, -100%)';
    return 'translate(-50%, -100%)';
  }, [tooltip.xAlign]);

  const caretLeftStyle = useMemo(() => {
    if (tooltip.xAlign === 'left') return '15%';
    if (tooltip.xAlign === 'right') return '85%';
    return '50%';
  }, [tooltip.xAlign]);

  return (
    <div className="bg-white border border-gray-100 rounded-[24px] p-6 shadow-sm flex flex-col justify-between">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-5 border-b border-gray-100">
        <div>
          <h3 className="text-lg font-bold text-[#111827] flex items-center gap-2">
            <FiUserPlus className="text-[#CC9900] text-xl" /> Daily Signups
          </h3>
          <p className="text-xs text-gray-400 mt-0.5">
            Daily user registrations across App and Web platforms.
          </p>
        </div>
      </div>

      {/* Chart */}
      <div className="h-[280px] w-full mt-4 relative flex items-center justify-center">
        {isLoading || !signupGraphData ? (
          <div className="flex flex-col items-center gap-3">
            <div className="w-10 h-10 border-[3px] border-gray-100 border-t-[#CC9900] rounded-full animate-spin" />
            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest animate-pulse">Collecting Report...</p>
          </div>
        ) : (
          <Bar data={signupGraphData} options={options} />
        )}

        {/* Custom Tooltip */}
        {tooltip.visible && (
          <div
            style={{
              position: 'absolute',
              left: tooltip.x,
              top: tooltip.y,
              transform: transformStyle,
              pointerEvents: 'none',
              zIndex: 50,
              background: 'rgba(15, 23, 42, 0.98)',
              backdropFilter: 'blur(8px)',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              borderRadius: '16px',
              padding: '12px 16px',
              color: '#F8FAFC',
              fontSize: '12px',
              boxShadow: '0 12px 30px rgba(0, 0, 0, 0.4), 0 4px 12px rgba(0, 0, 0, 0.3)',
              minWidth: '180px',
              fontFamily: "'Outfit', 'Inter', sans-serif"
            }}
          >
            {/* Date Header */}
            <div style={{ fontSize: '10px', fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '8px' }}>
              {tooltip.title}
            </div>

            {/* Stats */}
            {tooltip.details && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '20px' }}>
                  <span style={{ color: '#94A3B8', fontWeight: 500 }}>App Signups</span>
                  <span style={{ fontWeight: 600, color: '#F8FAFC' }}>{tooltip.details.app?.toLocaleString() ?? 0}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '20px' }}>
                  <span style={{ color: '#94A3B8', fontWeight: 500 }}>Web Signups</span>
                  <span style={{ fontWeight: 600, color: '#F8FAFC' }}>{tooltip.details.web?.toLocaleString() ?? 0}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '20px', borderTop: '1px solid rgba(255, 255, 255, 0.1)', paddingTop: '6px', marginTop: '2px' }}>
                  <span style={{ color: '#E2E8F0', fontWeight: 600 }}>Total Signups</span>
                  <span style={{ fontWeight: 800, color: '#CC9900' }}>{tooltip.details.total?.toLocaleString() ?? 0}</span>
                </div>
              </div>
            )}

            {/* Arrow caret */}
            <div style={{
              position: 'absolute',
              bottom: '-6px',
              left: caretLeftStyle,
              transform: 'translateX(-50%) rotate(45deg)',
              width: '12px',
              height: '12px',
              background: 'rgba(15, 23, 42, 0.98)',
              borderRight: '1px solid rgba(255, 255, 255, 0.15)',
              borderBottom: '1px solid rgba(255, 255, 255, 0.15)',
              pointerEvents: 'none',
            }} />
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(DailySignupChart);
